/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2025-01-07 14:26:58                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-01-27 13:19:07                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Components
  * Services
  * Validator
  * MachineForm: Machine create/edit form
*/

/* Imports */
import { t } from "i18next";
/***/

/* Components */
import DynamicListSelect from "../dynamic-list/DynamicListSelect";
/***/

/* Services */
import { validator } from "../../../services/form-validator/form-validator";
/***/

/* Validator */
const RELEASE_YEAR = /^\d{4}$/;

/** Quatre chiffres, ou vide : le champ n'est pas obligatoire. */
export function isReleaseYear(value: unknown): boolean {
  return value === undefined || value === null || value === "" || (typeof value === "string" && RELEASE_YEAR.test(value));
}

/** Ne conserve que les chiffres, sur 4 positions : une année, pas du texte. */
export function toReleaseYear(raw: string): string {
  return raw.replace(/\D/g, "").slice(0, 4);
}

export const MachineFormValidator = {
  serialNumber: (e: unknown) => e === undefined || e === null || validator.isString(e),
  releaseYear: isReleaseYear,
  type: (e: unknown) => validator.isNotNull(e),
  brand: (e: unknown) => validator.isNotNull(e)
};

const v = MachineFormValidator; // shorthand
/***/

/**
* MachineForm: Machine create/edit form
* @param data - previous date, edit purpose
* @param onChange - data change event
* @param onSubmit - on submit event
* @param submitted - subimitted form state
*/
export default function MachineForm({
  data,
  onChange,
  onSubmit,
  submitted
}: {
  data?: EasyTech.Machine,
  onChange: (name: string, value: any) => void,
  onSubmit: () => void,
  submitted?: boolean
}) {
  return (
    <>
      <div className="field">
        <label>{t("machine:form:fields:brand:label")} *</label>
        <DynamicListSelect  className={`${submitted && !validator.isFieldValid(data, "brand", v) ? "error" : ""}`}
                            data={data?.brand}
                            ressource="ma-brand"
                            onChange={(e) => onChange("brand", e)}/>
      </div>

      <div className="field">
        <label>{t("machine:form:fields:type:label")} *</label>
        <DynamicListSelect  className={`${submitted && !validator.isFieldValid(data, "type", v) ? "error" : ""}`}
                            data={data?.type}
                            ressource="ma-type"
                            onChange={(e) => onChange("type", e)}/>
      </div>

      <div className="field">
        <label>{t("machine:form:fields:serialNumber:label")}</label>
        <input  className="form-field"
                value={data?.serialNumber ?? ""}
                onChange={(e) => onChange("serialNumber", e.target.value)}
                placeholder="ex: 21ed5787e787874"/>
      </div>

      <div className="field">
        <label>{t("machine:form:fields:model:label")}</label>
        <input  className="form-field"
                value={data?.model ?? ""}
                onChange={(e) => onChange("model", e.target.value)}
                placeholder="ex: PC210"/>
      </div>

      <div className="field">
        <label>{t("machine:form:fields:releaseYear:label")}</label>
        <input  className={`form-field ${submitted && !validator.isFieldValid(data, "releaseYear", v) ? "error" : ""}`}
                type="number"
                inputMode="numeric"
                min={0}
                max={9999}
                step={1}
                value={data?.releaseYear ?? ""}
                onChange={(e) => onChange("releaseYear", toReleaseYear(e.target.value))}
                onKeyDown={(e) => {
                  if (["e", "E", "+", "-", ".", ","].includes(e.key)) e.preventDefault();
                }}
                placeholder="ex: 2010"/>
      </div>

      <div className="field">
        <label>{t("machine:form:fields:hourMeter:label")}</label>
        <input  className="form-field"
                value={data?.hourMeter ?? ""}
                onChange={(e) => onChange("hourMeter", e.target.value === "" ? undefined : Number(e.target.value))}
                placeholder="ex: 1234"/>
      </div>

      <div className="field">
        <label>{t("machine:form:fields:fleetNumber:label")}</label>
        <input  className="form-field"
                value={data?.fleetIdentifier ?? ""}
                onChange={(e) => onChange("fleetIdentifier", e.target.value)}
                placeholder="ex: 001"/>
      </div>

      <div className="field textarea">
        <label>{t("machine:form:fields:notes:label")}</label>
        <textarea   className="form-field"
                    value={data?.notes}
                    onChange={(e) => onChange("notes", e.target.value)}
                    placeholder="ex: ...">
        </textarea>
      </div>
    </>
  );
}
/***/
